/** Darf das Formular den Vorgang vorwählen? Nur, wenn das Backend ihn beim Absenden auch verknüpfen kann. */
export function isDeepLinkTicketLinkable(ctx) {
    if (!ctx.externalTicketId || ctx.alreadyLinked || ctx.isClosed || !ctx.unitId) {
        return false;
    }
    return ctx.linkable !== false;
}
/** Grund, warum der Vorgang nicht verknüpft werden kann (null = verknüpfbar); ältere Backends ohne Grund → abgeleitet. */
export function deepLinkNotLinkableReason(ctx) {
    if (isDeepLinkTicketLinkable(ctx)) {
        return null;
    }
    if (ctx.alreadyLinked) {
        return 'already-linked';
    }
    if (ctx.notLinkableReason) {
        return ctx.notLinkableReason;
    }
    if (ctx.isClosed) {
        return 'closed';
    }
    if (!ctx.unitId) {
        return 'no-unit';
    }
    return 'unknown';
}
const NOT_LINKABLE_TEXT = {
    'already-linked': 'Der Vorgang ist bereits mit einer INCON-Schadenmeldung verknüpft und wurde deshalb nicht vorausgewählt.',
    closed: 'Der Vorgang ist im CRM bereits geschlossen und kann deshalb nicht mit INCON verknüpft werden.',
    status: 'Der Status des Vorgangs im CRM lässt keine Verknüpfung mit INCON zu.',
    type: 'Der Vorgang hat nicht den Vorgangstyp „Versicherungsschaden INCON“ und kann deshalb nicht mit INCON verknüpft werden.',
    'no-unit': 'Der Vorgang ist im CRM keiner Einheit zugeordnet und kann deshalb nicht mit INCON verknüpft werden.',
    unknown: 'Der Vorgang kann nicht mit INCON verknüpft werden.',
};
/**
 * Gründe, die sich im CRM beheben lassen (danach verknüpft ein erneuter Klick auf den Link den Vorgang). Nicht `type`:
 * mit einer eigenen Typliste ohne „Versicherungsschaden INCON" endet der erneute Klick nach der Typänderung im 409.
 */
const FIXABLE_IN_CRM = ['closed', 'status', 'no-unit'];
export const DEEP_LINK_QUERY_PARAM = 'ctx';
/** Nur diese Vorbelegungsschlüssel werden in die URL übernommen (Suffix-Matching in createIncidentField). */
export const DEEP_LINK_PREFILL_KEYS = ['schadentag', 'hergang'];
/** Schutz vor überlangen URLs: längere Texte werden gekürzt. */
export const DEEP_LINK_PREFILL_MAX_LENGTH = 2000;
/** Token-Format des Backends: base64url(claims).base64url(signature) — alles andere gar nicht erst senden. */
const TOKEN_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
export function readDeepLinkToken(params) {
    const raw = params[DEEP_LINK_QUERY_PARAM];
    if (typeof raw !== 'string') {
        return null;
    }
    const token = raw.trim();
    return TOKEN_PATTERN.test(token) ? token : null;
}
export function buildDeepLinkResolveUrl(baseUrl, endpoint, token, profileId) {
    const params = new URLSearchParams();
    params.set(DEEP_LINK_QUERY_PARAM, token);
    if (profileId) {
        params.set('profileId', profileId);
    }
    return `${baseUrl}${endpoint}?${params.toString()}`;
}
export function sanitizePrefill(prefill) {
    const out = {};
    if (!prefill || typeof prefill !== 'object') {
        return out;
    }
    for (const key of DEEP_LINK_PREFILL_KEYS) {
        const value = prefill[key];
        if (typeof value !== 'string' || !value.trim()) {
            continue;
        }
        // Die Formularfelder sind einzeilige Eingaben: Zeilenumbrüche würden verworfen und Wörter verklebt
        const singleLine = value.replace(/\r?\n+/g, ' ').replace(/[ \t]{2,}/g, ' ').trim();
        if (!singleLine) {
            continue;
        }
        out[key] = singleLine.length > DEEP_LINK_PREFILL_MAX_LENGTH
            ? `${singleLine.slice(0, DEEP_LINK_PREFILL_MAX_LENGTH - 1)}…`
            : singleLine;
    }
    return out;
}
export class DeepLinkResolveError extends Error {
    constructor(status, message) {
        super(message);
        this.status = status;
        this.name = 'DeepLinkResolveError';
    }
}
export function deepLinkErrorMessage(status, detail) {
    switch (status) {
        case 400:
        case 401:
            return 'Der Link ist ungültig. Bitte öffnen Sie den Vorgang im CRM und verwenden Sie den dort hinterlegten Link.';
        case 403:
            return 'Der Link gehört zu einem anderen Mandanten. Bitte melden Sie sich mit dem passenden Lizenzschlüssel an.';
        case 404:
            return 'Der Vorgang wurde im CRM nicht gefunden. Bitte füllen Sie das Formular manuell aus.';
        case 409:
            return `${detail || 'Der Vorgang kann nicht mit INCON verknüpft werden (Vorgangstyp nicht freigegeben).'} Bitte prüfen Sie den Vorgangstyp im CRM oder füllen Sie das Formular manuell aus.`;
        case 410:
            // Einen neuen Link gibt es nicht: der Bot postet je Vorgang genau einen
            return 'Der Link ist abgelaufen. Bitte füllen Sie das Formular manuell aus.';
        case 502:
            return 'Der Vorgang konnte gerade nicht aus dem CRM geladen werden. Bitte versuchen Sie es später erneut oder füllen Sie das Formular manuell aus.';
        case 503:
            return 'Die Deep-Link-Funktion ist derzeit nicht verfügbar. Bitte füllen Sie das Formular manuell aus.';
        default:
            return `Der Vorgang konnte nicht geladen werden${detail ? ` (${detail})` : ''}. Bitte füllen Sie das Formular manuell aus.`;
    }
}
export function deepLinkAppliedMessage(ctx, result) {
    var _a;
    const parts = [];
    const ticketPreselected = (_a = result.ticketPreselected) !== null && _a !== void 0 ? _a : !ctx.alreadyLinked;
    const reason = ticketPreselected ? null : deepLinkNotLinkableReason(ctx);
    if (ctx.alreadyLinked) {
        parts.push('Zu diesem Vorgang existiert bereits eine Schadenmeldung bei INCON – der Vorgang wurde deshalb nicht vorausgewählt.');
        if (ctx.isClosed) {
            parts.push('Der Vorgang ist im CRM bereits geschlossen.');
        }
    }
    else if (reason) {
        parts.push(NOT_LINKABLE_TEXT[reason]);
    }
    const taken = [result.buildingPreselected ? 'Gebäude' : null, result.unitPreselected ? 'Einheit' : null, ticketPreselected ? 'Vorgang' : null].filter(Boolean);
    if (taken.length > 0) {
        parts.push(`${taken.join(', ').replace(/, ([^,]*)$/, ' und $1')} ${taken.length === 1 ? 'wurde' : 'wurden'} aus dem CRM übernommen.`);
    }
    if (ctx.unitId && !result.unitPreselected) {
        if (result.unitProblem === 'failed') {
            parts.push('Die Einheiten des Objekts konnten nicht geladen werden – bitte wählen Sie das Objekt erneut, dann werden sie neu geladen.');
        }
        else if (result.unitProblem === 'no-property') {
            parts.push('Das Objekt des Vorgangs ist nicht bekannt – bitte wählen Sie Objekt und Einheit manuell.');
        }
        else {
            parts.push('Die Einheit aus dem CRM wurde in der Einheitenliste nicht gefunden – bitte wählen Sie sie manuell.');
        }
    }
    if (!ticketPreselected) {
        // Ehrlich sagen, was beim Absenden passiert: ohne Verknüpfung legt das Backend einen neuen CRM-Vorgang an
        parts.push(ctx.alreadyLinked
            ? 'Wenn Sie das Formular trotzdem absenden, entstehen eine weitere Schadenmeldung und ein neuer Vorgang im CRM.'
            : 'Wenn Sie das Formular absenden, legt INCONconnect einen neuen Vorgang im CRM an.');
        if (reason && FIXABLE_IN_CRM.includes(reason)) {
            parts.push('Soll stattdessen dieser Vorgang verknüpft werden, passen Sie ihn im CRM an und öffnen Sie den Link danach erneut.');
        }
    }
    const todo = [result.buildingPreselected ? null : 'das zugehörige Gebäude (INCON)', 'Vertragsart', 'Schadenskategorie'].filter(Boolean);
    parts.push(`Bitte wählen Sie ${todo.join(', ').replace(/, ([^,]*)$/, ' und $1')}.`);
    return parts.join(' ');
}
/** Tooltip/Hinweis für Felder, die aus dem CRM-Vorgang übernommen und deshalb gesperrt sind. */
export function deepLinkLockHint(ctx) {
    const source = ctx.sourceSystem || 'CRM';
    const label = ctx.ticketNumber ? `Vorgang #${ctx.ticketNumber}` : 'Vorgang';
    return `Aus dem ${source}-${label} übernommen und deshalb gesperrt. Über „Vorbelegung aufheben" im Hinweis oben können Sie die Auswahl ändern.`;
}
